/**
 * Pipeline page — the CRM board for Module 4.
 *
 * Prospects audited by Inea land here, grouped by stage
 * (Froid → Tiède → Chaud → RDV → Client / Perdu). The user advances or
 * rewinds a prospect's stage and opens a detail drawer with the full audit.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  UserSearch,
  ChevronRight,
  ChevronLeft,
  Trash2,
  X,
  Inbox,
  Phone,
} from 'lucide-react';
import { usePipeline, useProspectDetail, type Prospect, type Stage } from '@/hooks/useProspect';
import { STAGE_ORDER, STAGE_META, TEMPERATURE_META, CATEGORY_META, scoreColor } from '@/lib/prospectMeta';
import { useIneaContext } from '@/components/IneaContext';
import { AuditReport } from '@/components/AuditReport';
import { ReplyComposer } from '@/components/ReplyComposer';
import { CallScriptModal } from '@/components/CallScriptModal';
import { Timeline } from '@/components/Timeline';
import { EngageProspectButton } from '@/components/EngageProspectButton';
import { ShareAuditButton } from '@/components/ShareAuditButton';
import { useCelebrate } from '@/hooks/useCelebrate';

export default function PipelinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { board, isLoading, update, remove } = usePipeline();
  const [openId, setOpenId] = useState<string | null>(null);
  const { setOpenProspectId } = useIneaContext();
  const celebrate = useCelebrate();

  // Wrap stage updates with celebration moments — first RDV booked + first
  // recruited partner produce a one-time confetti burst (Diamant #3).
  const moveProspect = (id: string, to: Stage) => {
    update.mutate({ id, patch: { stage: to } });
    if (to === 'meeting') celebrate('first-rdv');
    if (to === 'client') celebrate('first-recruit');
  };

  // Tell the floating Inea which prospect is in focus, so she can reason about
  // "ce prospect" / "lui" in conversational queries.
  useEffect(() => {
    setOpenProspectId(openId);
    return () => setOpenProspectId(null);
  }, [openId, setOpenProspectId]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-pink" size={30} />
      </div>
    );
  }

  const total = board?.total ?? 0;

  return (
    <div className="mx-auto max-w-[1180px] px-5 py-10 sm:px-8">
      <header className="mb-7 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div>
            <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">{t('pipeline.kicker')}</p>
            <h1 className="font-display text-[clamp(22px,3vw,30px)] font-800 tracking-[-0.025em]">{t('pipeline.title')}</h1>
          </div>
        </div>
        <button onClick={() => navigate('/audit')} className="btn-primary">
          <UserSearch size={16} /> {t('pipeline.addProspect')}
        </button>
      </header>

      {total === 0 ? (
        <EmptyPipeline onStart={() => navigate('/audit')} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {STAGE_ORDER.map((stage, i) => (
            <StageColumn
              key={stage}
              stage={stage}
              prospects={board?.board[stage] ?? []}
              delay={i * 0.05}
              onMove={moveProspect}
              onOpen={setOpenId}
            />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      <AnimatePresence>
        {openId && (
          <ProspectDrawer
            id={openId}
            onClose={() => setOpenId(null)}
            onDelete={(id) => {
              remove.mutate(id);
              setOpenId(null);
            }}
            onMove={moveProspect}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyPipeline({ onStart }: { onStart: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hi rounded-3xl p-10 text-center"
    >
      <span className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue to-violet text-white">
        <Inbox size={24} />
      </span>
      <h2 className="font-display text-xl font-800 tracking-[-0.02em]">
        {t('pipeline.emptyTitle')}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-[14px] leading-[1.6] text-ink/65">
        {t('pipeline.emptyBody')}
      </p>
      <button onClick={onStart} className="btn-primary mt-6">
        <UserSearch size={16} /> {t('pipeline.emptyCta')}
      </button>
    </motion.div>
  );
}

function StageColumn({
  stage,
  prospects,
  delay,
  onMove,
  onOpen,
}: {
  stage: Stage;
  prospects: Prospect[];
  delay: number;
  onMove: (id: string, to: Stage) => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const meta = STAGE_META[stage];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="rounded-3xl border border-ink/[0.06] bg-white/45 p-3.5"
    >
      <div className="mb-3 flex items-center gap-2 px-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
        <h3 className="font-display text-[14px] font-800 tracking-[-0.01em]">{t(meta.labelKey)}</h3>
        <span className="rounded-full bg-ink/[0.06] px-1.5 py-0.5 font-mono text-[10px] font-700 text-ink/55">
          {prospects.length}
        </span>
        <span className="ml-auto text-[10.5px] text-ink/40">{t(meta.hintKey)}</span>
      </div>
      <div className="space-y-2.5">
        {prospects.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-ink/[0.1] py-5 text-center text-[12px] text-ink/35">
            {t('prospect.emptyColumn')}
          </p>
        ) : (
          prospects.map((p) => (
            <ProspectCard key={p.id} prospect={p} onMove={onMove} onOpen={onOpen} />
          ))
        )}
      </div>
    </motion.div>
  );
}

function ProspectCard({
  prospect: p,
  onMove,
  onOpen,
}: {
  prospect: Prospect;
  onMove: (id: string, to: Stage) => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  const idx = STAGE_ORDER.indexOf(p.stage);
  const prev = idx > 0 ? STAGE_ORDER[idx - 1] : undefined;
  const next = idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : undefined;
  const temp = p.temperature ? TEMPERATURE_META[p.temperature] : null;
  const cat = p.category ? CATEGORY_META[p.category] : null;
  const name = p.display_name ?? p.handle ?? 'Prospect';

  return (
    <div className="group rounded-2xl border border-ink/[0.08] bg-white p-3 transition hover:border-pink/30 hover:shadow-[0_8px_20px_-12px_rgba(10,18,38,0.25)]">
      <button onClick={() => onOpen(p.id)} className="block w-full text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-display text-[13.5px] font-700 text-ink">{name}</p>
            {p.handle && p.display_name && (
              <p className="truncate font-mono text-[10.5px] text-ink/45">{p.handle}</p>
            )}
          </div>
          {p.score !== null && (
            <span
              className="shrink-0 rounded-lg px-1.5 py-0.5 font-display text-[12px] font-800"
              style={{ background: `${scoreColor(p.score)}1a`, color: scoreColor(p.score) }}
            >
              {p.score}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {temp && (
            <span className="text-[10.5px] font-600" style={{ color: temp.color }}>
              {temp.emoji} {t(temp.labelKey)}
            </span>
          )}
          {cat && <span className="text-[10.5px] text-ink/45">· {cat.emoji} {t(cat.labelKey)}</span>}
        </div>
      </button>

      {/* Stage controls */}
      <div className="mt-2.5 flex items-center gap-1 opacity-60 transition group-hover:opacity-100">
        <button
          disabled={!prev}
          onClick={() => prev && onMove(p.id, prev)}
          className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-ink/[0.05] text-ink/55 transition enabled:hover:bg-ink/10 disabled:opacity-30"
          aria-label={t("common.previousStage")}
        >
          <ChevronLeft size={13} />
        </button>
        <button
          disabled={!next}
          onClick={() => next && onMove(p.id, next)}
          className="inline-flex h-6 flex-1 items-center justify-center gap-1 rounded-lg bg-gradient-to-r from-blue/10 to-violet/10 text-[10.5px] font-700 text-violet transition enabled:hover:from-blue/20 enabled:hover:to-violet/20 disabled:opacity-30"
        >
          {next ? <>{t('pipeline.advance')} <ChevronRight size={12} /></> : t('pipeline.finalStage')}
        </button>
      </div>
    </div>
  );
}

function ProspectDrawer({
  id,
  onClose,
  onDelete,
  onMove,
}: {
  id: string;
  onClose: () => void;
  onDelete: (id: string) => void;
  onMove: (id: string, to: Stage) => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useProspectDetail(id);
  const [callOpen, setCallOpen] = useState(false);
  const [tab, setTab] = useState<'audit' | 'timeline'>('audit');
  // Show the call-script CTA only when a call makes sense.
  const showCallCta =
    !!data && (data.stage === 'warm' || data.stage === 'hot' || data.stage === 'meeting');
  const prospectName = data?.display_name ?? data?.handle ?? 'Prospect';

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-night/40 backdrop-blur-sm"
      />
      <motion.aside
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 280 }}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[560px] flex-col bg-cream shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-ink/[0.08] px-5 py-4">
          <h2 className="font-display text-[16px] font-800 tracking-[-0.02em]">{t('pipeline.prospectCard')}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-pink/10 text-pink-deep transition hover:bg-pink/20"
              aria-label={t("common.deleteProspect")}
            >
              <Trash2 size={15} />
            </button>
            <button
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-ink/[0.05] text-ink/60 transition hover:bg-ink/10"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-pink" size={26} />
            </div>
          ) : !data ? (
            <p className="py-20 text-center text-[13px] text-ink/50">{t('pipeline.prospectNotFound')}</p>
          ) : (
            <>
              {/* Stage switcher */}
              <div className="mb-5 flex flex-wrap gap-1.5">
                {STAGE_ORDER.map((s) => {
                  const m = STAGE_META[s];
                  const active = data.stage === s;
                  return (
                    <button
                      key={s}
                      onClick={() => onMove(id, s)}
                      className="rounded-full px-2.5 py-1 text-[11px] font-700 transition"
                      style={
                        active
                          ? { background: m.color, color: '#fff' }
                          : { background: `${m.color}14`, color: m.color }
                      }
                    >
                      {t(m.labelKey)}
                    </button>
                  );
                })}
              </div>

              {/* Engagement Inea — Module 10 */}
              <EngageProspectButton prospectId={id} prospectName={prospectName} />

              {/* Share audit card — T3 viralité */}
              {data?.analysis && (
                <ShareAuditButton
                  data={{
                    prospect_name: prospectName,
                    prospect_handle: data.handle ?? null,
                    platform: data.platform ?? null,
                    temperature: (data.temperature ?? 'cold') as 'cold' | 'warm' | 'hot',
                    score: data.score ?? 0,
                    category: data.category ?? '—',
                    headline: data.analysis.headline ?? '',
                    top_strengths: data.analysis.buying_signals?.slice(0, 3) ?? [],
                    top_pain_points: data.analysis.pain_points?.slice(0, 3) ?? [],
                  }}
                />
              )}

              {/* Call-script CTA — for prospects you're likely to call */}
              {showCallCta && (
                <button
                  onClick={() => setCallOpen(true)}
                  className="mb-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-blue/30 bg-gradient-to-br from-blue/[0.06] to-violet/[0.05] px-4 py-3.5 text-left transition hover:from-blue/[0.10] hover:to-violet/[0.08]"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue to-violet text-white">
                      <Phone size={15} />
                    </span>
                    <span>
                      <span className="font-display text-[14px] font-800 tracking-[-0.015em]">
                        Préparer l'appel avec {prospectName}
                      </span>
                      <p className="mt-0.5 text-[12px] text-ink/55">
                        {t('pipeline.callScriptHint')}
                      </p>
                    </span>
                  </span>
                  <span className="font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-blue">
                    IA
                  </span>
                </button>
              )}

              {/* DM helper — paste an incoming message, get 3 AI replies */}
              <div className="mb-5">
                <ReplyComposer prospectId={id} />
              </div>

              {/* Audit / Timeline tabs */}
              <div className="mb-3 flex gap-1 rounded-full bg-ink/[0.04] p-1">
                <button
                  onClick={() => setTab('audit')}
                  className={`flex-1 rounded-full px-3 py-1.5 text-[12px] font-700 transition ${
                    tab === 'audit' ? 'bg-white text-ink shadow-sm' : 'text-ink/60'
                  }`}
                >
                  Audit IA
                </button>
                <button
                  onClick={() => setTab('timeline')}
                  className={`flex-1 rounded-full px-3 py-1.5 text-[12px] font-700 transition ${
                    tab === 'timeline' ? 'bg-white text-ink shadow-sm' : 'text-ink/60'
                  }`}
                >
                  Historique
                </button>
              </div>

              {tab === 'audit' ? (
                data.analysis ? (
                  <AuditReport analysis={data.analysis} />
                ) : (
                  <p className="text-[13px] text-ink/55">{t("pipeline.noAnalysis")}</p>
                )
              ) : (
                <Timeline prospectId={id} />
              )}
            </>
          )}
        </div>
      </motion.aside>

      {/* Discovery-call script modal — lazy-generates when opened */}
      <CallScriptModal
        prospectId={id}
        prospectName={prospectName}
        open={callOpen}
        onClose={() => setCallOpen(false)}
      />
    </>
  );
}
